/**
 * Mirrors `partbay-api/.../vendorRules.ts` for vendor equality used in NEX PO scoping.
 * Keep in sync when server heuristics change.
 */

const MEGAVENDOR_SUBSTRINGS = ['ebay', 'lkq', 'amazon', 'advance', 'oreilly', 'autozone', 'keystone'] as const;

function normalizeForMegavendorMatch(s: string): string {
    return s
        .toLowerCase()
        .normalize('NFKD')
        .replace(/\p{M}/gu, '')
        .replace(/[''′]/g, '')
        .replace(/[^\w\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function shareMegavendorKeyword(a: string | null | undefined, b: string | null | undefined): boolean {
    const na = normalizeForMegavendorMatch(a ?? '');
    const nb = normalizeForMegavendorMatch(b ?? '');
    if (!na || !nb) return false;
    for (const token of MEGAVENDOR_SUBSTRINGS) {
        if (na.includes(token) && nb.includes(token)) return true;
    }
    return false;
}

export function normalizeVendorName(vendorName: string): string {
    if (!vendorName) return '';
    let normalized = vendorName.toLowerCase().trim();
    normalized = normalized.replace(/\s*&\s*/g, ' and ');
    const suffixes = [/\s+inc\.?$/i, /\s+llc\.?$/i, /\s+ltd\.?$/i, /\s+corp\.?$/i, /\s+corporation$/i, /\s+company$/i, /\s+co\.?$/i, /\s+incorporated$/i];
    for (const suffix of suffixes) {
        normalized = normalized.replace(suffix, '');
    }
    normalized = normalized.replace(/[.,;:]/g, ' ').replace(/\s+/g, ' ').trim();
    return normalized;
}

export function extractKeyWords(vendorName: string): string[] {
    const normalized = normalizeVendorName(vendorName);
    return normalized
        .split(/\s+(?:of|and|&)\s+|\s+/)
        .filter((word) => word.length > 2)
        .map((word) => word.trim())
        .filter((word) => word.length > 0);
}

function areVendorNamesSimilar(invoiceVendor: string | null, nexVendor: string | null): boolean {
    if (!invoiceVendor || !nexVendor) return false;
    if (shareMegavendorKeyword(invoiceVendor, nexVendor)) return true;
    const normalizedInvoice = normalizeVendorName(invoiceVendor);
    const normalizedNex = normalizeVendorName(nexVendor);
    if (normalizedInvoice === normalizedNex) return true;
    if (normalizedInvoice.includes(normalizedNex) || normalizedNex.includes(normalizedInvoice)) return true;
    const invoiceWords = extractKeyWords(invoiceVendor);
    const nexWords = extractKeyWords(nexVendor);
    if (invoiceWords.length === 0 || nexWords.length === 0) {
        return normalizedInvoice.includes(normalizedNex) || normalizedNex.includes(normalizedInvoice);
    }
    const brandWords = new Set([
        'chrysler',
        'dodge',
        'jeep',
        'ram',
        'fiat',
        'ford',
        'chevrolet',
        'chevy',
        'toyota',
        'honda',
        'nissan',
        'hyundai',
        'kia',
        'subaru',
        'mazda',
        'volkswagen',
        'vw',
    ]);
    const commonBrand = invoiceWords.find(
        (w) => brandWords.has(w.toLowerCase()) && nexWords.some((n) => n.toLowerCase() === w.toLowerCase())
    );
    if (commonBrand) {
        return true;
    }
    const allInvoiceWordsInNex = invoiceWords.every((word) =>
        nexWords.some((nexWord) => nexWord.includes(word) || word.includes(nexWord))
    );
    const allNexWordsInInvoice = nexWords.every((word) =>
        invoiceWords.some((invWord) => invWord.includes(word) || word.includes(invWord))
    );
    if (allInvoiceWordsInNex && allNexWordsInInvoice) return true;
    if (invoiceWords.length >= 2 && nexWords.length >= 2) {
        const coreInvoiceWords = invoiceWords.slice(0, 2);
        const coreNexWords = nexWords.slice(0, 2);
        const coreMatches =
            coreInvoiceWords.every((word) =>
                coreNexWords.some((nexWord) => nexWord === word || nexWord.includes(word) || word.includes(nexWord))
            ) &&
            coreNexWords.every((word) =>
                coreInvoiceWords.some((invWord) => invWord === word || invWord.includes(word) || word.includes(invWord))
            );
        if (coreMatches) return true;
    }
    const commonWords = invoiceWords.filter((invWord) =>
        nexWords.some((nexWord) => invWord === nexWord || invWord.includes(nexWord) || nexWord.includes(invWord))
    );
    const totalUniqueWords = new Set([...invoiceWords, ...nexWords]).size;
    const overlapRatio = totalUniqueWords > 0 ? commonWords.length / totalUniqueWords : 0;
    return overlapRatio >= 0.5 && commonWords.length >= Math.min(invoiceWords.length, nexWords.length) * 0.5;
}

/** Same as server `vendorNamesMatchForProbableMatch`. */
export function vendorNamesMatchForProbableMatch(
    invoiceVendor: string | null | undefined,
    nexPoVendorName: string | null | undefined
): boolean {
    const inv = (invoiceVendor ?? '').trim();
    const nex = (nexPoVendorName ?? '').trim();
    if (!inv || !nex) return false;
    return areVendorNamesSimilar(inv, nex);
}
